import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useQuery } from '@tanstack/react-query';
import {
  getUser,
  getUserCallHistory,
  getUserProfileData,
} from '@/services/user-profile-service/user-profile.service';

// Bundles the three independent endpoints the Profile tab needs into a
// single hook so the consumer doesn't have to thread three loading states.
// All three are gated on `userId` — when the conversation has no linked
// User row (anonymous caller), nothing fires and the tab simply doesn't
// render.
//
// The base /users/:id endpoint serializes the User record; profile_data
// returns the latest profile-type Document content; call_history returns a
// pre-rolled-up recent-calls envelope. Failures are isolated per query so a
// 404 on profile_data (no document yet) doesn't blank the whole tab.
export const useUserProfile = (userId: number | null | undefined) => {
  const enabled = !!userId;

  const profile = useQuery({
    queryKey: ReactQueryKeys.USER_PROFILE(userId ?? undefined),
    queryFn: () => getUser(userId!),
    enabled,
    staleTime: 60_000,
  });

  const profileData = useQuery({
    queryKey: ReactQueryKeys.USER_PROFILE_DATA(userId ?? undefined),
    queryFn: () => getUserProfileData(userId!),
    enabled,
    staleTime: 60_000,
    retry: false,
  });

  const callHistory = useQuery({
    queryKey: ReactQueryKeys.USER_CALL_HISTORY(userId ?? undefined),
    queryFn: () => getUserCallHistory(userId!),
    enabled,
    staleTime: 30_000,
  });

  return {
    user: profile.data,
    isLoadingUser: profile.isPending,
    profileData: profileData.data,
    isLoadingProfileData: profileData.isPending,
    callHistory: callHistory.data,
    isLoadingCallHistory: callHistory.isPending,
    isError: profile.isError || callHistory.isError,
  };
};
